import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Checkbox } from "@/components/ui/checkbox";
import { ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

export default function LegalGate({ children }) {
  const { user, acceptLegal, reconsent } = useAuth();
  const [terms, setTerms] = useState(false);
  const [privacy, setPrivacy] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [ai, setAi] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!user) return children;
  const needsReconsent = !!(user.legal_accepted && user.needs_reconsent);
  if (user.legal_accepted && !needsReconsent) return children;

  const submit = async () => {
    setLoading(true);
    try {
      if (needsReconsent) {
        await reconsent({ terms, privacy, marketing, ai_processing: ai });
        toast.success("Thank you — your acceptance is up to date.");
      } else {
        await acceptLegal({ terms, privacy, marketing, ai_processing: ai });
        toast.success("Welcome to Suffle Online!");
      }
    } catch (e) {
      toast.error("Could not save your acceptance. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur">
      <div className="w-full max-w-lg rounded-2xl bg-white p-7 shadow-2xl" data-testid="legal-gate">
        <Logo variant="md" />
        <div className="mt-5 flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-blue-500" />
          <h2 className="text-xl font-bold text-slate-800">{needsReconsent ? "We've updated our legal terms" : "Before you continue"}</h2>
        </div>
        <p className="mt-1 text-sm text-slate-500">{needsReconsent ? "Our Terms & Conditions or Privacy Policy have changed. Please review and re-accept to keep using Suffle Online." : "Please review and accept to start using your AI Secretary."}</p>

        <div className="mt-5 space-y-3">
          <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 p-3">
            <Checkbox checked={terms} onCheckedChange={setTerms} data-testid="accept-terms" className="mt-0.5" />
            <span className="text-sm text-slate-600">I agree to the <Link to="/legal/terms-conditions" target="_blank" rel="noopener noreferrer" className="font-medium text-blue-600">Terms &amp; Conditions</Link></span>
          </label>
          <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 p-3">
            <Checkbox checked={privacy} onCheckedChange={setPrivacy} data-testid="accept-privacy" className="mt-0.5" />
            <span className="text-sm text-slate-600">I have read and agree to the <Link to="/legal/privacy-policy" target="_blank" rel="noopener noreferrer" className="font-medium text-blue-600">Privacy Policy</Link></span>
          </label>
          <div className="pt-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Optional</div>
          <label className="flex cursor-pointer items-start gap-3">
            <Checkbox checked={marketing} onCheckedChange={setMarketing} data-testid="accept-marketing" className="mt-0.5" />
            <span className="text-sm text-slate-500">Send me offers and product updates</span>
          </label>
          <label className="flex cursor-pointer items-start gap-3">
            <Checkbox checked={ai} onCheckedChange={setAi} data-testid="accept-ai" className="mt-0.5" />
            <span className="text-sm text-slate-500">Allow AI processing of my authorized data to personalize my experience</span>
          </label>
        </div>

        <button onClick={submit} disabled={!terms || !privacy || loading} data-testid="legal-continue"
          className="mt-6 w-full rounded-xl brand-gradient-bg py-3 font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-50">
          {loading ? "Saving..." : needsReconsent ? "Accept & Continue" : "Continue"}
        </button>
      </div>
    </div>
  );
}
